import React, { useState, useEffect, useRef } from "react";
import { BiCheckbox } from "react-icons/bi";
import { FaBars, FaTimes } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

function WorkWeDo() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);
  const [services, setServices] = useState([]);
  const [sectionTitle, setSectionTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const greenColor = "#70B444";
  const API_URL = "https://backend.dlapakistan.com/api/about/work-we-do/";

  useEffect(() => {
    // Fetch data from API
    const fetchServices = async () => {
      try {
        setLoading(true);
        const response = await axios.get(API_URL);
        
        if (response.data && response.data.services) {
          setServices(response.data.services);
          setSectionTitle(response.data.section_title || "Work We Do");
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching services:", err);
        setError("Failed to load services. Please try again later.");
        
        // Fallback data in case of error
        setServices([
          {
            id: 1,
            title: "Education",
            description: "Educational services and programs",
            slug: "education",
            order: 0,
            is_active: true,
            image: "/education.jpg"
          },
          {
            id: 2,
            title: "Regulation",
            description: "Regulatory services and compliance",
            slug: "regulation",
            order: 1,
            is_active: true,
            image: "/regulation.jpg"
          },
          {
            id: 3,
            title: "Membership Support",
            description: "Support for our members",
            slug: "membership-support",
            order: 2,
            is_active: true,
            image: "/MembershipSupport.jpg"
          }
        ]);
        setSectionTitle("Work We Do");
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  useEffect(() => {
    // Header की height पता करें
    const updateHeaderHeight = () => {
      const header = document.querySelector('header') || 
                    document.querySelector('.header') ||
                    document.querySelector('#header');
      
      if (header) {
        setHeaderHeight(header.offsetHeight);
      } else {
        // Fallback height अगर header नहीं मिले
        if (windowWidth < 640) {
          setHeaderHeight(80);
        } else if (windowWidth < 768) {
          setHeaderHeight(96);
        } else {
          setHeaderHeight(120);
        }
      }
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      updateHeaderHeight();
      if (window.innerWidth > 1029) {
        setIsSidebarOpen(false);
      }
    };

    // Initial और resize पर update
    updateHeaderHeight();
    window.addEventListener("resize", handleResize);
    
    // Resize observer for dynamic header changes
    const observer = new ResizeObserver(updateHeaderHeight);
    const headerElement = headerRef.current || 
                         document.querySelector('header');
    if (headerElement) observer.observe(headerElement);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (headerElement) observer.unobserve(headerElement);
    };
  }, [windowWidth]);

  const isMobileView = windowWidth <= 1029;

  // Sidebar links
  const sidebarLinks = [
    { text: "About Us", border: "#ccc", showIcon: false, path: "/about" },
    { text: "Meet the Board", border: "#ccc", showIcon: true, path: "/about/meet-the-board" },
    { text: "Work We Do", border: null, showIcon: true, path: "/about/work-we-do" },
    { text: "Education", border: null, showIcon: false, path: "/about/work-we-do/education" },
    { text: "Regulation", border: null, showIcon: false, path: "/about/work-we-do/regulation" },
    { text: "Membership Support", border: "#ccc", showIcon: false, path: "/about/work-we-do/membership-support" },
    { text: "Our Policies", border: "#ccc", showIcon: true, path: "/about/our-policies" },
    { text: "Become a Member", border: "#ccc", showIcon: true, path: "/about/become-member" },
    { text: "DLA Fellowship", border: null, showIcon: true, path: "/about/dla-fellowship" },
    { text: "DLA Fellowship Awards", border: null, showIcon: false, path: "/about/dla-fellowship/awards" },
    { text: "DLA Special Recognition Award", border: "#ccc", showIcon: false, path: "/about/dla-fellowship/special-recognition" },
  ];

  const handleSidebarClick = (path) => {
    navigate(path);
    if (windowWidth <= 1029) {
      setIsSidebarOpen(false);
    }
  };

  const handleCardClick = (service) => {
    // If service has a direct URL, navigate to it
    if (service.url) {
      // Check if it's an external URL
      if (service.url.startsWith('http://') || service.url.startsWith('https://')) {
        window.open(service.url, '_blank');
      } else {
        // Internal URL
        navigate(service.url);
      }
    } else {
      // Fallback to slug-based navigation
      navigate(`/about/work-we-do/${service.slug}`);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleBreadcrumbClick = (path) => {
    navigate(path);
  };

  // Function to get image URL (handle absolute and relative URLs)
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "/default-service.jpg";
    
    // If it's already a full URL or starts with http
    if (imagePath.startsWith('http') || imagePath.startsWith('//')) {
      return imagePath;
    }
    
    // If it starts with /media/, add the base URL
    if (imagePath.startsWith('/media/')) {
      return `https://backend.dlapakistan.com${imagePath}`;
    }
    
    // If it's just a local path
    return imagePath;
  };

  // CSS class for mobile overlay
  const mobileOverlayClass = isMobileView && isSidebarOpen ? "pt-0" : "";

  if (loading) {
    return (
      <div className="w-full px-4 mt-52 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        <div className="text-center py-20">
          <p className="mt-3 text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  if (error && services.length === 0) {
    return (
      <div className="w-full px-4 mt-52 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        <div className="text-center py-20">
          <div className="text-red-500 text-4xl mb-3">⚠️</div>
          <h3 className="text-red-600 mb-2">Error Loading Content</h3>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-2 bg-[#70B444] text-white rounded hover:bg-[#5d9a3a]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`w-full min-h-screen ${mobileOverlayClass}`}
      ref={headerRef}
    >
      
      {/* Main Container */}
      <div className="w-full px-4 mt-52 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        
        {/* Breadcrumb section */}
        <div className="flex flex-wrap items-center text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => handleBreadcrumbClick("/")}
          >
            Home
          </span> 
          <span className="mx-2">»</span>
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => handleBreadcrumbClick("/about")}
          >
            About The DLA
          </span>
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium">
            Work We Do
          </span>
        </div>

        {/* Main Content Layout */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative pb-8 md:pb-12">
          
          {/* DESKTOP SIDEBAR */}
          {!isMobileView && (
            <div className="w-full lg:w-72 xl:w-80 shrink-0 bg-[#f7f9fc] p-4 md:p-5 rounded-lg shadow-sm h-fit sticky top-24">
              <div className="bg-[#70B444] text-white p-3 text-center mb-4">
                <h3 className="m-0 text-base lg:text-lg font-semibold">About The DLA</h3>
              </div>
              {sidebarLinks.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleSidebarClick(item.path)}
                  className={`flex items-center py-2.5 text-[#70B444] font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-2 text-sm lg:text-base ${
                    item.border ? "border-b border-gray-200" : ""
                  } ${location.pathname === item.path ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" : ""}`}
                >
                  {item.showIcon && <BiCheckbox className="mr-2 text-lg flex-shrink-0" />}
                  <span className="break-words">{item.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* MAIN CONTENT AREA */}
          <div className={`flex-1 ${isMobileView ? 'p-0' : 'p-2 md:p-4'} ${
            isMobileView && isSidebarOpen ? "opacity-30 pointer-events-none" : "opacity-100"
          }`}>
            
            <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug">
                {sectionTitle}
              </h1>
              
              {isMobileView && (
                <button
                  onClick={toggleSidebar}
                  className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-3 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm font-semibold shrink-0"
                >
                  {isSidebarOpen ? <FaTimes /> : <FaBars />}
                  <span>{isSidebarOpen ? "Close" : "Menu"}</span>
                </button>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-yellow-700 mb-0">
                  ⚠️ {error} Showing cached/fallback data.
                </p>
              </div>
            )}

            {/* Cards */}
            {services.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No services available at the moment.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {services.map((service) => (
                  <div key={service.id} className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col h-full">
                    <img
                      className="w-full h-64 object-cover"
                      src={getImageUrl(service.image)}
                      alt={service.title}
                      onError={(e) => {
                        // Fallback image if original fails to load
                        e.target.src = getImageUrl(`/${service.slug}.jpg`);
                      }}
                    />
                    <div className="pt-4 pb-5 flex flex-col items-center flex-grow">
                      <h5 className="mb-4 font-bold" style={{ color: greenColor, fontSize: "1.25rem" }}>
                        {service.title}
                      </h5>
                      {/* {service.description && (
                        <p className="mb-4 text-gray-600">
                          {service.description.length > 100 
                            ? `${service.description.substring(0, 100)}...` 
                            : service.description}
                        </p>
                      )} */}
                    
                      <div className="mt-2 h-0.5 bg-[#70B444] w-24 mx-auto"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* MOBILE SIDEBAR */}
          {isMobileView && (
            <div
              className={`fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out ${
                isSidebarOpen ? "translate-x-0" : "translate-x-full"
              }`}
              style={{ top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }}
            >
              <div className="flex flex-col h-full">
                <div className="p-4 border-b flex justify-between items-center bg-[#70B444] text-white">
                  <span className="font-bold text-lg">Navigation</span>
                  <FaTimes 
                    className="cursor-pointer text-2xl" 
                    onClick={toggleSidebar} 
                  />
                </div>
                <div className="overflow-y-auto flex-1 p-2">
                  {sidebarLinks.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleSidebarClick(item.path)}
                      className={`p-3 md:p-4 text-base border-b border-gray-100 flex items-center hover:bg-gray-50 ${
                        location.pathname === item.path 
                          ? "text-[#70B444] font-semibold bg-green-50 border-l-4 border-l-[#70B444]" 
                          : "text-gray-700"
                      }`}
                    >
                      {item.showIcon && <BiCheckbox className="mr-3 text-xl text-[#70B444] flex-shrink-0" />}
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Overlay */}
          {isMobileView && isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
              style={{ top: `${headerHeight}px` }}
              onClick={toggleSidebar}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default WorkWeDo;